import {queryClient} from '@common/http/query-client';
import {Button} from '@ui/buttons/button';
import {Trans} from '@ui/i18n/trans';
import {ErrorIcon} from '@ui/icons/material/Error';
import {IllustratedMessage} from '@ui/images/illustrated-message';
import {useState} from 'react';
import {useRouteError} from 'react-router';

export function PageErrorMessage() {
  const [isRetrying, setIsRetrying] = useState(false);
  // React Router exposes the caught error via this hook. We surface it so
  // the user sees what actually failed instead of a generic message.
  const routeError = useRouteError() as any;
  const [showStack, setShowStack] = useState(false);

  const handleRetry = async () => {
    setIsRetrying(true);
    await queryClient.resetQueries();
    // Soft-reload the current route by re-navigating to it. Many errors come
    // from a stale chunk; a hard reload is the most reliable recovery.
    setTimeout(() => window.location.reload(), 200);
  };

  const errorMessage =
    routeError?.message ||
    routeError?.toString?.() ||
    'Unknown error';
  const errorStack = routeError?.stack || '';

  return (
    <IllustratedMessage
      className="pt-40"
      image={<div><ErrorIcon size="xl" /></div>}
      imageHeight="h-auto"
      title={<Trans message="There was an issue loading this page" />}
      description={
        <div className="mx-auto mt-4 max-w-680 space-y-8 text-left">
          <pre className="overflow-auto rounded-lg border bg-alt p-12 text-xs text-danger">
            {errorMessage}
          </pre>
          {errorStack && (
            <details className="text-xs text-muted" open={showStack} onToggle={e => setShowStack((e.target as HTMLDetailsElement).open)}>
              <summary className="cursor-pointer"><Trans message="Show stack trace" /></summary>
              <pre className="mt-8 max-h-300 overflow-auto rounded border bg-alt p-8 text-2xs">{errorStack}</pre>
            </details>
          )}
        </div>
      }
      action={
        <Button variant="outline" onClick={handleRetry} disabled={isRetrying}>
          <Trans message="Reload page" />
        </Button>
      }
    />
  );
}
